import { ref } from 'vue'
import { Capacitor } from '@capacitor/core'
import { purchasePremiumNative, presentWebPaywall, ensureWebPaywallReady, readActiveEntitlement } from '@/services/revenuecat.service'
import { useAppContextStore } from '@/stores/appContext'
import { paywallOfferingId } from '@/services/businessCohort'
import { useAuthStore } from '@/stores/auth'
import { supabase } from '@/lib/supabase'
import { toast } from 'vue-sonner'
import { i18n } from '@/i18n'
import { useAnalytics } from '@/composables/useAnalytics'
import { handlePaywallCancelled } from '@/composables/usePaywallDismissReason'

const analytics = useAnalytics()

/**
 * Qui arrive au paywall, pour le distinguer dans Amplitude.
 *
 * Les états d'essai (`trial_available`, `trial`, `trial_ended`) sont partis
 * avec le reverse trial. Reste ce qui compte dans le modèle par profil : le
 * foyer a-t-il déjà payé, et sinon combien d'enfants a-t-il, puisque c'est le
 * deuxième profil qui l'amène ici.
 */
function paywallTrialState(appContext: ReturnType<typeof useAppContextStore>): 'paid' | 'free_one_child' | 'free_multi_child' {
  if (appContext.isPaidPremium) return 'paid'
  return appContext.availableChildren.length > 1 ? 'free_multi_child' : 'free_one_child'
}

/**
 * On native (iOS/Android): RevenueCatUI.presentPaywall() — native paywall.
 * On web: RevenueCat JS SDK presentPaywall() rendered in a modal container.
 */
export function usePremiumPurchase() {
  const isNative = Capacitor.isNativePlatform()
  const showWebPaywall = ref(false)
  const loading = ref(false)

  /**
   * `surface` nomme la fonctionnalité qui a amené le parent devant le paywall.
   * Sans elle, toutes les portes d'entrée payantes se ressemblent dans
   * Amplitude et on ne sait pas laquelle vend. Elle est requise pour qu'un
   * nouveau point d'appel ne puisse pas rejoindre l'entonnoir sans être nommé.
   */
  async function startPurchase(surface: string): Promise<boolean> {
    const appContext = useAppContextStore()
    const authStore = useAuthStore()
    const householdId = appContext.currentHousehold?.id
    const userId = authStore.user?.id
    if (!householdId || !userId) return false

    // Émis avant la présentation : c'est bien l'affichage du paywall qu'on
    // mesure, pas son issue (l'achat a ses propres événements RevenueCat).
    analytics.logPaywallViewed(surface, isNative ? 'native' : 'web', undefined, paywallTrialState(appContext))

    if (isNative) {
      loading.value = true
      try {
        // Le paywall suit la cohorte : un foyer du modèle par profil voit
        // l'offering de son contrat, l'ancien contrat garde le Current. C'est
        // l'app qui tranche, avec `households.business_cohort` déjà en main,
        // plutôt qu'une règle de ciblage posée dans le dashboard, invisible
        // d'ici et sans test.
        const outcome = await purchasePremiumNative(
          householdId,
          userId,
          paywallOfferingId(appContext.currentHousehold),
        )
        if (outcome.kind === 'cancelled') {
          // Un paywall qui se referme sans vendre ne prouve pas que le parent
          // n'a rien. `cancelled` couvre aussi NOT_PRESENTED et les erreurs
          // avalées par le SDK, et surtout une RESTAURATION n'émet aucun
          // webhook : la base peut donc rester gratuite alors que RevenueCat
          // accorde déjà l'entitlement. Sans cette relecture, chaque porte
          // payante rouvrait un paywall qui n'a plus rien à vendre, en boucle.
          const entitlement = await readActiveEntitlement()
          if (entitlement.active && !appContext.isPaidPremium) {
            await activatePremium(appContext, userId)
            showPurchaseSuccessToast()
            return true
          }

          // Le seul CANCELLED non ambigu de l'app : côté web, `false` couvre
          // aussi bien l'annulation qu'une erreur du SDK, et Amplitude y est
          // de toute façon désactivé (natif uniquement). Poser la question là
          // où on ne peut rien mesurer n'ajouterait que de la friction.
          void handlePaywallCancelled(surface, 'native')
          return false
        }

        // Rien à restaurer : le foyer reste tel qu'il est. Pas de question sur
        // la fermeture non plus, le parent n'a pas renoncé à un achat.
        if (outcome.kind === 'nothing_restored') return false

        // Achat OU restauration reconnus par le SDK. On fait ouvrir Harmonia+
        // par le serveur, qui relit RevenueCat, sans attendre le webhook : en
        // sandbox celui-ci met plus de dix secondes, et le parent voyait son
        // achat aboutir puis le paywall se rouvrir sur une porte qu'il venait
        // de payer. Le webhook reste la source de vérité pour la suite,
        // renouvellements et expirations, que le téléphone ne voit jamais.
        await activatePremium(appContext, userId)

        showPurchaseSuccessToast()
        return true
      } catch (err) {
        console.error('[Premium] Purchase error:', err)
        return false
      } finally {
        loading.value = false
      }
    } else {
      // Pas de caisse web configurée : ne pas ouvrir la modale. Elle poserait
      // un calque plein écran vide, le temps que renderWebPaywall échoue et le
      // referme — un éclair blanc, sans un mot d'explication.
      // Attendre l'init en vol plutôt que d'en lire un instantané : elle part
      // sans `await` depuis le listener d'auth, et un compte neuf touche ce
      // bouton dans les secondes qui suivent son arrivée dans l'app.
      if (!(await ensureWebPaywallReady())) {
        showWebPurchaseUnavailableToast()
        return false
      }
      // Web: show modal with RC paywall container
      showWebPaywall.value = true
      return false // result comes via renderWebPaywall
    }
  }

  async function renderWebPaywall(container: HTMLElement): Promise<boolean> {
    const appContext = useAppContextStore()
    const authStore = useAuthStore()
    const userId = authStore.user?.id
    if (!userId) return false

    loading.value = true
    try {
      const purchased = await presentWebPaywall(container, paywallOfferingId(appContext.currentHousehold))
      if (purchased && userId) {
        await waitForPremiumActivation(appContext, userId)
        showPurchaseSuccessToast()
      }
      return purchased
    } catch (err) {
      console.error('[Premium] Web paywall error:', err)
      return false
    } finally {
      loading.value = false
      showWebPaywall.value = false
    }
  }

  function closeWebPaywall() {
    showWebPaywall.value = false
  }

  return {
    isNative,
    showWebPaywall,
    loading,
    startPurchase,
    renderWebPaywall,
    closeWebPaywall,
  }
}

// Poll until the RevenueCat webhook has flipped the household to premium.
// The webhook is the source of truth for premium_until, so we wait for it
// rather than writing the row from the client. Returns true if activation
// was observed within the polling window.
//
// On interroge LA LIGNE, pas le contexte. `forceReloadContext` remet à null le
// foyer, l'enfant courant et les deux listes avant de recharger : le rejouer
// dix fois de suite faisait clignoter l'app entière pendant l'attente, un écran
// sans enfants par seconde, juste pour relire un booléen. Le contexte n'est
// rechargé qu'une fois, quand la réponse est connue.
async function waitForPremiumActivation(
  appContext: ReturnType<typeof useAppContextStore>,
  userId: string,
  { maxAttempts = 10, delayMs = 1000 }: { maxAttempts?: number; delayMs?: number } = {}
): Promise<boolean> {
  const householdId = appContext.currentHousehold?.id ?? null
  for (let i = 0; i < maxAttempts; i++) {
    if (await readPremiumFlagFromDb(householdId, userId)) {
      await appContext.forceReloadContext(userId)
      return true
    }
    await new Promise((r) => setTimeout(r, delayMs))
  }
  console.warn('[Premium] Webhook did not activate premium within polling window')
  return false
}

/**
 * Le drapeau premium tel qu'il est en base, sans reconstruire le contexte.
 *
 * `maybeSingle` et non `single` : pendant la fenêtre de sondage, zéro ligne est
 * un état normal et ne doit pas remonter en 406.
 */
async function readPremiumFlagFromDb(householdId: string | null, userId: string): Promise<boolean> {
  const rows = supabase.from('households').select('is_premium')
  const { data, error } = householdId
    ? await rows.eq('id', householdId).maybeSingle()
    : await rows.eq('owner_id', userId).limit(1).maybeSingle()
  if (error) {
    console.error('[Premium] premium poll failed:', error)
    return false
  }
  return Boolean((data as any)?.is_premium)
}

/**
 * Demande au serveur d'aligner le foyer sur ce que RevenueCat accorde à ce
 * compte. Rend true quand Harmonia+ est ouvert en base à son retour.
 *
 * L'app écrivait auparavant `is_premium` elle-même, sur la foi du SDK. Tout
 * compte pouvait donc s'ouvrir Harmonia+ par l'API, et une restauration qui ne
 * trouvait rien l'a fait par accident (HARMONIA-74). Le téléphone ne fait plus
 * que demander : la fonction `sync-premium` lit RevenueCat et écrit le foyer
 * (HARMONIA-81). Elle n'attend pas le webhook, donc un achat sandbox, dont le
 * webhook est lent, ouvre Harmonia+ tout de suite comme avant.
 */
async function syncPremiumWithServer(): Promise<boolean> {
  try {
    const { data, error } = await supabase.functions.invoke('sync-premium', { body: {} })
    if (error) {
      console.error('[Premium] sync-premium failed:', error)
      return false
    }
    return data?.ok === true && data?.active === true
  } catch (err) {
    console.error('[Premium] sync-premium failed:', err)
    return false
  }
}

/**
 * Après un achat ou une restauration reconnus par le SDK : faire ouvrir
 * Harmonia+ par le serveur, et à défaut attendre que le webhook le fasse.
 */
async function activatePremium(
  appContext: ReturnType<typeof useAppContextStore>,
  userId: string,
): Promise<void> {
  if (await syncPremiumWithServer()) {
    await appContext.forceReloadContext(userId)
    return
  }
  // Fonction injoignable ou RevenueCat en panne : le webhook reste là.
  if (!(await waitForPremiumActivation(appContext, userId))) {
    await appContext.forceReloadContext(userId)
  }
}

function showPurchaseSuccessToast() {
  const t = i18n.global.t
  toast.success(t('premiumThankYou.title'), {
    description: t('premiumThankYou.subtitle'),
    duration: 5000,
  })
}

/**
 * La PWA ne sait pas encaisser (clé Web Billing absente, ou clé de boutique de
 * test dans un build de production, refusée par initRevenueCat). Dire quoi
 * faire vaut mieux qu'un paywall qui s'ouvre et se referme.
 */
function showWebPurchaseUnavailableToast() {
  const t = i18n.global.t
  toast(t('premiumWebUnavailable.title'), {
    description: t('premiumWebUnavailable.subtitle'),
    duration: 6000,
  })
}

export function showPremiumExpiredToast() {
  const t = i18n.global.t
  toast(t('premiumExpired.title'), {
    description: t('premiumExpired.subtitle'),
    duration: 6000,
  })
}

/**
 * Fin de l'essai. Message distinct de premiumExpired : parler d'« abonnement
 * terminé » à un parent qui n'a jamais payé serait faux, et lui laisserait
 * croire qu'on lui a prélevé quelque chose.
 */
export function showTrialEndedToast() {
  const t = i18n.global.t
  toast(t('trialEnded.title'), {
    description: t('trialEnded.subtitle'),
    duration: 6000,
  })
}
